/**
 * Yatırımcı Paneli Servis Katmanı
 *
 * Tüm yatırımcı paneli veritabanı işlemlerini merkezi olarak yönetir.
 * Supabase client üzerinden RLS korumalı sorgular çalıştırır.
 *
 * @module investorService
 */

import { supabase } from '../lib/supabase';

const investorService = {
  // ===========================================================
  // Dashboard İstatistikleri
  // ===========================================================

  /**
   * Yatırımcı dashboard'u için özet istatistikleri döndürür.
   * @param {string} userId - Kullanıcı UUID'si
   * @returns {{ data: object|null, error: object|null }}
   */
  async getDashboardStats(userId) {
    try {
      // Paralel olarak tüm istatistikleri çek
      const [
        favoritesResult,
        investmentsResult,
        pendingDocsResult,
        totalAmountResult,
        recentViewsResult,
      ] = await Promise.all([
        // Toplam favori sayısı
        supabase
          .from('investor_favorites')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId),

        // Aktif yatırım sayısı (cancelled ve completed hariç)
        supabase
          .from('investor_investments')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .not('status', 'in', '("cancelled","completed")'),

        // Bekleyen belge sayısı
        supabase
          .from('investor_documents')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('status', 'pending'),

        // Toplam yatırım tutarı (completed olanlar)
        supabase
          .from('investor_transactions')
          .select('amount')
          .eq('user_id', userId)
          .eq('status', 'completed')
          .in('transaction_type', ['deposit', 'installment', 'final_payment']),

        // Son 30 günde görüntülenen ilan sayısı
        supabase
          .from('investor_property_views')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .gte('viewed_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()),
      ]);

      // Toplam tutarı hesapla
      const totalInvested = (totalAmountResult.data || []).reduce(
        (sum, row) => sum + parseFloat(row.amount || 0),
        0
      );

      return {
        data: {
          totalFavorites: favoritesResult.count || 0,
          activeInvestments: investmentsResult.count || 0,
          pendingDocuments: pendingDocsResult.count || 0,
          totalInvested,
          recentViews: recentViewsResult.count || 0,
        },
        error: null,
      };
    } catch (error) {
      console.error('getDashboardStats error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Favoriler
  // ===========================================================

  /**
   * Kullanıcının tüm favorilerini property bilgileriyle birlikte döndürür.
   * @param {string} userId
   * @param {number} [limit=50]
   * @param {number} [offset=0]
   * @returns {{ data: array|null, error: object|null }}
   */
  async getFavorites(userId, limit = 50, offset = 0) {
    try {
      const { data, error, count } = await supabase
        .from('investor_favorites')
        .select('*, property:properties(*)', { count: 'exact' })
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      return { data, count, error };
    } catch (error) {
      console.error('getFavorites error:', error);
      return { data: null, count: 0, error };
    }
  },

  /**
   * Bir ilanı favorilere ekler.
   * @param {string} userId
   * @param {string} propertyId
   * @param {string} [notes]
   * @returns {{ data: object|null, error: object|null }}
   */
  async addFavorite(userId, propertyId, notes = null) {
    try {
      const { data, error } = await supabase
        .from('investor_favorites')
        .upsert(
          { user_id: userId, property_id: propertyId, notes },
          { onConflict: 'user_id,property_id' }
        )
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('addFavorite error:', error);
      return { data: null, error };
    }
  },

  /**
   * Bir ilanı favorilerden kaldırır.
   * @param {string} userId
   * @param {string} propertyId
   * @returns {{ data: object|null, error: object|null }}
   */
  async removeFavorite(userId, propertyId) {
    try {
      const { data, error } = await supabase
        .from('investor_favorites')
        .delete()
        .eq('user_id', userId)
        .eq('property_id', propertyId);

      return { data, error };
    } catch (error) {
      console.error('removeFavorite error:', error);
      return { data: null, error };
    }
  },

  /**
   * Bir ilanın favori olup olmadığını kontrol eder.
   * @param {string} userId
   * @param {string} propertyId
   * @returns {{ data: boolean, error: object|null }}
   */
  async isFavorite(userId, propertyId) {
    try {
      const { data, error } = await supabase
        .from('investor_favorites')
        .select('id')
        .eq('user_id', userId)
        .eq('property_id', propertyId)
        .maybeSingle();

      return { data: !!data, error };
    } catch (error) {
      console.error('isFavorite error:', error);
      return { data: false, error };
    }
  },

  // ===========================================================
  // Görüntüleme Geçmişi
  // ===========================================================

  /**
   * Kullanıcının ilan görüntüleme geçmişini döndürür.
   * @param {string} userId
   * @param {number} [limit=20]
   * @returns {{ data: array|null, error: object|null }}
   */
  async getViewHistory(userId, limit = 20) {
    try {
      const { data, error } = await supabase
        .from('investor_property_views')
        .select('*, property:properties(*)')
        .eq('user_id', userId)
        .order('viewed_at', { ascending: false })
        .limit(limit);

      return { data, error };
    } catch (error) {
      console.error('getViewHistory error:', error);
      return { data: null, error };
    }
  },

  /**
   * Bir ilan görüntülemesini kaydet veya mevcut kaydı güncelle.
   * UPSERT kullanarak aynı ilan için view_count artırır ve viewed_at günceller.
   * @param {string} userId
   * @param {string} propertyId
   * @returns {{ data: object|null, error: object|null }}
   */
  async recordView(userId, propertyId) {
    try {
      // Önce mevcut kaydı kontrol et
      const { data: existing } = await supabase
        .from('investor_property_views')
        .select('id, view_count')
        .eq('user_id', userId)
        .eq('property_id', propertyId)
        .maybeSingle();

      if (existing) {
        // Mevcut kaydı güncelle
        const { data, error } = await supabase
          .from('investor_property_views')
          .update({
            view_count: (existing.view_count || 1) + 1,
            viewed_at: new Date().toISOString(),
          })
          .eq('id', existing.id)
          .select()
          .single();

        return { data, error };
      }

      // Yeni kayıt oluştur
      const { data, error } = await supabase
        .from('investor_property_views')
        .insert({
          user_id: userId,
          property_id: propertyId,
        })
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('recordView error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Yatırımlar
  // ===========================================================

  /**
   * Kullanıcının yatırımlarını property bilgileriyle birlikte döndürür.
   * @param {string} userId
   * @param {string|null} [status=null] - Filtrelenecek durum
   * @returns {{ data: array|null, error: object|null }}
   */
  async getInvestments(userId, status = null) {
    try {
      let query = supabase
        .from('investor_investments')
        .select('*, property:properties(*)')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      console.error('getInvestments error:', error);
      return { data: null, error };
    }
  },

  /**
   * Tek bir yatırımın detayını belgeler ve işlemlerle birlikte döndürür.
   * @param {string} investmentId
   * @returns {{ data: object|null, error: object|null }}
   */
  async getInvestmentById(investmentId) {
    try {
      const [investmentResult, documentsResult, transactionsResult] = await Promise.all([
        supabase
          .from('investor_investments')
          .select('*, property:properties(*)')
          .eq('id', investmentId)
          .single(),

        supabase
          .from('investor_documents')
          .select('*')
          .eq('investment_id', investmentId)
          .order('uploaded_at', { ascending: false }),

        supabase
          .from('investor_transactions')
          .select('*')
          .eq('investment_id', investmentId)
          .order('transaction_date', { ascending: false }),
      ]);

      if (investmentResult.error) {
        return { data: null, error: investmentResult.error };
      }

      return {
        data: {
          ...investmentResult.data,
          documents: documentsResult.data || [],
          transactions: transactionsResult.data || [],
        },
        error: null,
      };
    } catch (error) {
      console.error('getInvestmentById error:', error);
      return { data: null, error };
    }
  },

  /**
   * Yeni bir yatırım kaydı oluşturur.
   * @param {{ user_id: string, property_id: string, status?: string, offer_amount?: number, currency?: string, notes?: string }} data
   * @returns {{ data: object|null, error: object|null }}
   */
  async createInvestment(data) {
    try {
      const { data: investment, error } = await supabase
        .from('investor_investments')
        .insert({
          user_id: data.user_id,
          property_id: data.property_id,
          status: data.status || 'interested',
          offer_amount: data.offer_amount || null,
          currency: data.currency || 'GBP',
          notes: data.notes || null,
        })
        .select('*, property:properties(*)')
        .single();

      return { data: investment, error };
    } catch (error) {
      console.error('createInvestment error:', error);
      return { data: null, error };
    }
  },

  /**
   * Mevcut bir yatırım kaydını günceller.
   * @param {string} investmentId
   * @param {object} data - Güncellenecek alanlar
   * @returns {{ data: object|null, error: object|null }}
   */
  async updateInvestment(investmentId, data) {
    try {
      const { data: investment, error } = await supabase
        .from('investor_investments')
        .update(data)
        .eq('id', investmentId)
        .select('*, property:properties(*)')
        .single();

      return { data: investment, error };
    } catch (error) {
      console.error('updateInvestment error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Belgeler
  // ===========================================================

  /**
   * Kullanıcının belgelerini listeler. Opsiyonel olarak yatırıma göre filtreler.
   * @param {string} userId
   * @param {string|null} [investmentId=null]
   * @returns {{ data: array|null, error: object|null }}
   */
  async getDocuments(userId, investmentId = null) {
    try {
      let query = supabase
        .from('investor_documents')
        .select('*')
        .eq('user_id', userId)
        .order('uploaded_at', { ascending: false });

      if (investmentId) {
        query = query.eq('investment_id', investmentId);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      console.error('getDocuments error:', error);
      return { data: null, error };
    }
  },

  /**
   * Bir belgeyi Supabase Storage'a yükler ve veritabanı kaydını oluşturur.
   * @param {{ user_id: string, investment_id?: string, document_type: string, file: File }} data
   * @returns {{ data: object|null, error: object|null }}
   */
  async uploadDocument(data) {
    try {
      const { user_id, investment_id, document_type, file } = data;

      // 1. Dosyayı Supabase Storage'a yükle
      const fileExt = file.name.split('.').pop();
      const fileName = `${user_id}/${Date.now()}_${document_type}.${fileExt}`;

      const { data: storageData, error: storageError } = await supabase.storage
        .from('investor-documents')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (storageError) {
        return { data: null, error: storageError };
      }

      // 2. Public URL al
      const { data: urlData } = supabase.storage
        .from('investor-documents')
        .getPublicUrl(storageData.path);

      // 3. Veritabanı kaydı oluştur
      const { data: document, error: dbError } = await supabase
        .from('investor_documents')
        .insert({
          user_id,
          investment_id: investment_id || null,
          document_type,
          file_name: file.name,
          file_url: urlData.publicUrl,
          file_size: file.size,
        })
        .select()
        .single();

      if (dbError) {
        // Veritabanı kaydı başarısız olursa yüklenen dosyayı temizle
        await supabase.storage.from('investor-documents').remove([storageData.path]);
        return { data: null, error: dbError };
      }

      return { data: document, error: null };
    } catch (error) {
      console.error('uploadDocument error:', error);
      return { data: null, error };
    }
  },

  /**
   * Bir belgeyi storage ve veritabanından siler.
   * @param {string} documentId
   * @returns {{ data: object|null, error: object|null }}
   */
  async deleteDocument(documentId) {
    try {
      // Önce belge bilgisini al (file_url'den path çıkarmak için)
      const { data: doc, error: fetchError } = await supabase
        .from('investor_documents')
        .select('file_url')
        .eq('id', documentId)
        .single();

      if (fetchError) {
        return { data: null, error: fetchError };
      }

      // Storage'dan sil (URL'den path çıkar)
      if (doc?.file_url) {
        const storagePath = doc.file_url.split('/investor-documents/').pop();
        if (storagePath) {
          await supabase.storage.from('investor-documents').remove([storagePath]);
        }
      }

      // Veritabanından sil
      const { data, error } = await supabase
        .from('investor_documents')
        .delete()
        .eq('id', documentId);

      return { data, error };
    } catch (error) {
      console.error('deleteDocument error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // İşlemler (Transactions)
  // ===========================================================

  /**
   * Kullanıcının finansal işlemlerini listeler. Opsiyonel olarak yatırıma göre filtreler.
   * @param {string} userId
   * @param {string|null} [investmentId=null]
   * @returns {{ data: array|null, error: object|null }}
   */
  async getTransactions(userId, investmentId = null) {
    try {
      let query = supabase
        .from('investor_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('transaction_date', { ascending: false });

      if (investmentId) {
        query = query.eq('investment_id', investmentId);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      console.error('getTransactions error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Profil
  // ===========================================================

  /**
   * Yatırımcı profilini user_profiles ve investor_profiles join ederek döndürür.
   * @param {string} userId
   * @returns {{ data: object|null, error: object|null }}
   */
  async getInvestorProfile(userId) {
    try {
      const [userProfileResult, investorProfileResult] = await Promise.all([
        supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .single(),

        supabase
          .from('investor_profiles')
          .select('*')
          .eq('id', userId)
          .single(),
      ]);

      if (userProfileResult.error) {
        return { data: null, error: userProfileResult.error };
      }

      return {
        data: {
          ...userProfileResult.data,
          investor: investorProfileResult.data || null,
        },
        error: null,
      };
    } catch (error) {
      console.error('getInvestorProfile error:', error);
      return { data: null, error };
    }
  },

  /**
   * Yatırımcı profilini günceller.
   * user_profiles ve investor_profiles tablolarını ayrı ayrı günceller.
   * @param {string} userId
   * @param {{ full_name?: string, phone?: string, country?: string, city?: string, nationality?: string, investment_budget?: number, preferred_locations?: string[], preferred_property_types?: string[], investment_goal?: string }} data
   * @returns {{ data: object|null, error: object|null }}
   */
  async updateInvestorProfile(userId, data) {
    try {
      // user_profiles alanları
      const userFields = {};
      if (data.full_name !== undefined) userFields.full_name = data.full_name;
      if (data.phone !== undefined) userFields.phone = data.phone;
      if (data.avatar_url !== undefined) userFields.avatar_url = data.avatar_url;

      // investor_profiles alanları
      const investorFields = {};
      const investorKeys = [
        'country', 'city', 'nationality', 'date_of_birth',
        'investment_budget', 'preferred_locations',
        'preferred_property_types', 'investment_goal',
      ];
      for (const key of investorKeys) {
        if (data[key] !== undefined) {
          investorFields[key] = data[key];
        }
      }

      const promises = [];

      if (Object.keys(userFields).length > 0) {
        promises.push(
          supabase
            .from('user_profiles')
            .update(userFields)
            .eq('id', userId)
            .select()
            .single()
        );
      } else {
        promises.push(Promise.resolve({ data: null, error: null }));
      }

      if (Object.keys(investorFields).length > 0) {
        promises.push(
          supabase
            .from('investor_profiles')
            .update(investorFields)
            .eq('id', userId)
            .select()
            .single()
        );
      } else {
        promises.push(Promise.resolve({ data: null, error: null }));
      }

      const [userResult, investorResult] = await Promise.all(promises);

      if (userResult.error) {
        return { data: null, error: userResult.error };
      }
      if (investorResult.error) {
        return { data: null, error: investorResult.error };
      }

      return {
        data: {
          ...userResult.data,
          investor: investorResult.data,
        },
        error: null,
      };
    } catch (error) {
      console.error('updateInvestorProfile error:', error);
      return { data: null, error };
    }
  },
};

export default investorService;
